<template>
  <div class="ppv-packages-container">
    <h3 class="section-title">PPV Packages</h3>

    <!-- Header with search and add -->
    <div class="ppv-header">
      <div class="search-box">
        <input
          v-model="filter"
          type="text"
          placeholder="Search packages..."
          class="search-input"
        />
      </div>
      <div class="header-actions">
        <div class="filter-pills">
          <button :class="['pill', statusFilter === '*' && 'active']" @click="statusFilter = '*'">All</button>
          <button :class="['pill', statusFilter === 'active' && 'active']" @click="statusFilter = 'active'">Active</button>
          <button :class="['pill', statusFilter === 'inactive' && 'active']" @click="statusFilter = 'inactive'">Inactive</button>
        </div>
        <button class="add-btn" @click="startCreate">+ New Package</button>
      </div>
    </div>

    <!-- Editor panel (create or edit) -->
    <transition name="slide-fade">
      <div v-if="showEditor && form" class="package-card editing">
        <div class="editor-grid">
          <div class="field">
            <label class="config-label">Name</label>
            <input v-model="form.name" type="text" placeholder="Package name..." class="text-input" ref="nameInput"/>
          </div>
          <div class="field">
            <label class="config-label">Slug</label>
            <input v-model="form.slug" type="text" placeholder="auto-generated from name" class="text-input"/>
          </div>
          <div class="field full">
            <label class="config-label">Description</label>
            <textarea v-model="form.description" rows="2" placeholder="Optional description shown on the basket page" class="text-input"></textarea>
          </div>
          <div class="field">
            <label class="config-label">Price Tier</label>
            <select v-model="form.price_tier_id" class="text-input">
              <option :value="null">Default tier</option>
              <option v-for="t in activeTiers" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div class="field field-toggle">
            <label class="toggle-label" @click="form.is_active = !form.is_active">
              <div :class="['toggle', form.is_active && 'on']"><div class="toggle-knob"></div></div>
              <span>Active</span>
            </label>
          </div>
        </div>

        <!-- Items manager -->
        <div class="items-manager">
          <label class="config-label">Videos in package ({{ form.items.length }})</label>
          <div v-if="form.items.length === 0" class="items-empty">No videos yet — add some below.</div>
          <transition-group name="list" tag="div" class="items-list">
            <div v-for="(it, idx) in form.items" :key="it.media_id" class="item-row">
              <span class="item-title">{{ it.title || 'Untitled' }}</span>
              <span class="badge type">{{ it.type }}</span>
              <button class="btn-remove" @click="removeItem(idx)">Remove</button>
            </div>
          </transition-group>
          <button class="add-video-btn" @click="selectItemVisible = true">+ Add Video</button>
        </div>

        <div class="card-actions editor-actions">
          <button class="btn-cancel" @click="closeEditor">Cancel</button>
          <button class="btn-save" @click="save">{{ editingId ? 'Save' : 'Create' }}</button>
        </div>
      </div>
    </transition>

    <!-- Packages list -->
    <div class="packages-list">
      <transition-group name="list" tag="div">
        <div
          v-for="pkg in filteredPackages"
          :key="pkg.id"
          class="package-card viewable"
          @click="startEdit(pkg)"
        >
          <div class="card-row">
            <div class="card-main">
              <div class="package-name">
                {{ pkg.name }}
                <span :class="['badge', pkg.is_active ? 'active' : 'inactive']">{{ pkg.is_active ? 'Active' : 'Inactive' }}</span>
              </div>
              <div class="package-meta">
                <span>{{ pkg.item_count }} video{{ pkg.item_count === 1 ? '' : 's' }}</span>
                <span> · {{ pkg.price_tier_id ? tierName(pkg.price_tier_id) : 'Default tier' }}</span>
                <span v-if="pkg.slug"> · /{{ pkg.slug }}</span>
              </div>
            </div>
            <div class="card-actions">
              <button class="btn-delete" @click.stop="confirmDelete(pkg)">Delete</button>
            </div>
          </div>
        </div>
      </transition-group>

      <div v-if="filteredPackages.length === 0 && !showEditor" class="empty-state">
        <p v-if="filter || statusFilter !== '*'">No packages match your filters</p>
        <p v-else>No PPV packages yet</p>
        <button class="add-btn" @click="startCreate">+ Create First Package</button>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <transition name="modal-fade">
      <div v-if="deletingPackage" class="modal-overlay" @click.self="deletingPackage = null">
        <div class="confirm-modal">
          <h3>Delete Package</h3>
          <p>Are you sure you want to delete <strong>{{ deletingPackage.name }}</strong>?</p>
          <p class="warning">If anyone is mid-rental, the package is deactivated instead of removed.</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="deletingPackage = null">Cancel</button>
            <button class="btn-delete" @click="deletePackage">Delete</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Select video modal -->
    <SelectItem v-model="selectItemVisible" title="Add Video to Package" :lang="lang" types="video" @selected="onItemSelected"/>
  </div>
</template>

<script>
import SelectItem from '../components/SelectItem.vue'

export default {
  components: { SelectItem },

  data() {
    return {
      packages: [],
      priceTiers: [],
      filter: '',
      statusFilter: '*',

      // Single editor for both create and edit
      showEditor: false,
      editingId: null, // null = creating
      form: null,

      deletingPackage: null,
      selectItemVisible: false
    }
  },

  computed: {
    lang() {
      return Object.keys(this.$store.state.app.languages || {})[0] || 'en'
    },

    activeTiers() {
      return (this.priceTiers || []).filter(t => t.is_active)
    },

    filteredPackages() {
      return this.packages.filter(pkg => {
        if (this.statusFilter === 'active' && !pkg.is_active) return false
        if (this.statusFilter === 'inactive' && pkg.is_active) return false
        if (this.filter && !(pkg.name || '').toLowerCase().includes(this.filter.toLowerCase())) return false
        return true
      })
    }
  },

  methods: {
    loadPackages() {
      fetch('/admin/api/v2/ppv-packages')
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => { this.packages = resp.data || [] })
        .catch(() => this.$root.app.showAlert('Error loading packages', 'danger'))
    },

    loadTiers() {
      fetch('/admin/api/v2/ppv-price-tiers')
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => { this.priceTiers = resp.data || [] })
        .catch(() => {})
    },

    tierName(id) {
      const t = (this.priceTiers || []).find(t => t.id === id)
      return t ? t.name : 'Unknown tier'
    },

    emptyPackage() {
      return { name: '', description: '', slug: '', price_tier_id: null, is_active: true, items: [] }
    },

    startCreate() {
      this.editingId = null
      this.form = this.emptyPackage()
      this.showEditor = true
      this.$nextTick(() => { if (this.$refs.nameInput) this.$refs.nameInput.focus() })
    },

    startEdit(pkg) {
      fetch(`/admin/api/v2/ppv-packages/${pkg.id}`)
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => {
          const p = resp.data
          this.editingId = p.id
          this.form = {
            name: p.name || '',
            description: p.description || '',
            slug: p.slug || '',
            price_tier_id: p.price_tier_id || null,
            is_active: !!p.is_active,
            items: (p.items || []).map(i => ({ media_id: i.media_id, title: i.title, type: i.type }))
          }
          this.showEditor = true
          window.scrollTo({ top: 0, behavior: 'smooth' })
        })
        .catch(() => this.$root.app.showAlert('Error loading package', 'danger'))
    },

    closeEditor() {
      this.showEditor = false
      this.editingId = null
      this.form = null
    },

    buildPayload(pkg) {
      return {
        name: pkg.name,
        description: pkg.description,
        slug: pkg.slug || null,
        price_tier_id: pkg.price_tier_id || null,
        is_active: pkg.is_active,
        item_ids: (pkg.items || []).map(i => i.media_id)
      }
    },

    save() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$root.app.showAlert('Please enter a package name', 'warning')
        return
      }

      const url = this.editingId ? `/admin/api/v2/ppv-packages/${this.editingId}` : '/admin/api/v2/ppv-packages'
      const method = this.editingId ? 'PUT' : 'POST'
      const wasEditing = !!this.editingId

      fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.buildPayload(this.form) })
      })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(() => {
          this.$root.app.showAlert(wasEditing ? 'Package updated' : 'Package created', 'success')
          this.closeEditor()
          this.loadPackages()
          this.$store.dispatch('media/fetchPpvPackages')
        })
        .catch(() => this.$root.app.showAlert('Error saving package', 'danger'))
    },

    onItemSelected(item) {
      if (!item || !this.form) return
      if (this.form.items.some(i => i.media_id === item.id)) {
        this.$root.app.showAlert('That video is already in the package', 'warning')
        return
      }
      this.form.items.push({
        media_id: item.id,
        title: (item.meta && item.meta.title) || 'Untitled',
        type: item.type
      })
    },

    removeItem(idx) {
      this.form.items.splice(idx, 1)
    },

    confirmDelete(pkg) {
      this.deletingPackage = pkg
    },

    deletePackage() {
      const id = this.deletingPackage.id
      fetch(`/admin/api/v2/ppv-packages/${id}`, { method: 'DELETE' })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => {
          const deactivated = resp && resp.data === 'deactivated'
          this.$root.app.showAlert(
            deactivated ? 'Package had active purchases — deactivated instead of deleted' : 'Package deleted',
            deactivated ? 'warning' : 'success'
          )
          this.deletingPackage = null
          this.loadPackages()
          this.$store.dispatch('media/fetchPpvPackages')
        })
        .catch(() => this.$root.app.showAlert('Error deleting package', 'danger'))
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('PPV Packages', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.loadTiers()
    this.loadPackages()
  }
}
</script>

<style scoped>
.ppv-packages-container {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

/* Header */
.ppv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box { flex: 1; max-width: 280px; }

.search-input,
.text-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.search-input:focus,
.text-input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(255, 255, 255, 0.1);
}

.search-input::placeholder,
.text-input::placeholder { color: #94A3B8; }

.header-actions { display: flex; align-items: center; gap: 1rem; }

.filter-pills {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.pill {
  padding: 0.35rem 0.75rem;
  border: none;
  background: transparent;
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.pill:hover { color: #fff; background: rgba(255, 255, 255, 0.05); }
.pill.active { background: rgba(255, 255, 255, 0.1); color: #fff; font-weight: 500; }

.add-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3); }

/* Cards */
.packages-list { display: flex; flex-direction: column; gap: 0.5rem; }

.package-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.package-card.viewable { cursor: pointer; }
.package-card.viewable:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.package-card.editing {
  background: rgba(255, 255, 255, 0.08);
  border-color: #20B4FF;
  box-shadow: 0 0 0 1px #20B4FF, 0 10px 25px rgba(0, 0, 0, 0.2);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}

.card-row { display: flex; padding: 1.25rem 1.5rem; align-items: center; gap: 2rem; }
.card-main { flex: 1; min-width: 250px; }

.package-name {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.package-meta { font-size: 0.8rem; color: #94A3B8; margin-top: 0.25rem; }

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.active { background: rgba(40, 167, 69, 0.15); color: #28a745; border: 1px solid rgba(40, 167, 69, 0.2); }
.badge.inactive { background: rgba(108, 117, 125, 0.15); color: #94A3B8; border: 1px solid rgba(108, 117, 125, 0.2); }
.badge.type { background: rgba(32, 180, 255, 0.12); color: #20B4FF; border: 1px solid rgba(32, 180, 255, 0.2); }

/* Editor */
.editor-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field.full { grid-column: 1 / -1; }
.field-toggle { justify-content: flex-end; }

.config-label { font-size: 0.8rem; color: #E2E8F0; }

/* Toggle */
.toggle-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; user-select: none; }
.toggle-label span { font-size: 0.85rem; color: #E2E8F0; }
.toggle {
  width: 36px; height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  position: relative;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.toggle.on { background: #20B4FF; border-color: #20B4FF; }
.toggle-knob {
  width: 16px; height: 16px;
  background: #fff; border-radius: 50%;
  position: absolute; top: 1px; left: 1px;
  transition: all 0.2s;
}
.toggle.on .toggle-knob { left: 17px; }

/* Items manager */
.items-manager {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.25rem;
}

.items-empty { color: #94A3B8; font-size: 0.85rem; margin: 0.5rem 0; }
.items-list { display: flex; flex-direction: column; gap: 0.4rem; margin: 0.5rem 0; }

.item-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
}

.item-title { flex: 1; color: #fff; font-size: 0.9rem; }

.btn-remove {
  background: transparent;
  color: #adb5bd;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}
.btn-remove:hover { background: #dc3545; color: #fff; }

.add-video-btn {
  margin-top: 0.5rem;
  padding: 0.4rem 0.85rem;
  background: rgba(32, 180, 255, 0.12);
  color: #20B4FF;
  border: 1px solid rgba(32, 180, 255, 0.25);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}
.add-video-btn:hover { background: rgba(32, 180, 255, 0.2); color: #fff; }

/* Actions */
.card-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
.editor-actions { justify-content: flex-end; }

.btn-save, .btn-cancel, .btn-delete {
  padding: 0.45rem 0.9rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-save { background: #28a745; color: #fff; }
.btn-save:hover { background: #218838; }
.btn-cancel { background: #3a3f47; color: #c9d1d9; }
.btn-cancel:hover { background: #4a5058; color: #fff; }
.btn-delete { background: transparent; color: #adb5bd; }
.btn-delete:hover { background: #dc3545; color: #fff; }

/* Empty state */
.empty-state { text-align: center; padding: 3rem; color: #9ca3af; }
.empty-state p { margin-bottom: 1rem; }

/* Modal */
.modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}
.confirm-modal {
  background: #2a2e35;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 380px;
  width: 90%;
  border: 1px solid #3a3f47;
}
.confirm-modal h3 { margin: 0 0 0.75rem; color: #fff; font-size: 1.1rem; }
.confirm-modal p { color: #c9d1d9; margin-bottom: 0.5rem; font-size: 0.9rem; }
.confirm-modal .warning { color: #ffc107; font-size: 0.8rem; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.25rem; }

/* Transitions */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter, .slide-fade-leave-to { opacity: 0; transform: translateY(-10px); }
.list-enter-active, .list-leave-active { transition: all 0.2s ease; }
.list-enter, .list-leave-to { opacity: 0; }
.list-move { transition: transform 0.2s ease; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.15s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

@media (max-width: 768px) {
  .ppv-header { flex-direction: column; align-items: stretch; }
  .search-box { max-width: none; }
  .header-actions { justify-content: space-between; }
  .editor-grid { grid-template-columns: 1fr; }
  .card-row { flex-direction: column; align-items: stretch; gap: 1rem; }
}
</style>
